import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { api, clearClientSession } from '../api/http';
import { buildAuthProviderSession } from './sessionHydration';

const AuthCtx = createContext(null);

const AUTHDBG_ENABLED =
  String(process.env.REACT_APP_AUTHDBG || '').toLowerCase() === '1' ||
  String(process.env.REACT_APP_AUTHDBG || '').toLowerCase() === 'true';

const authDbg = (label, data = {}) => {
  if (!AUTHDBG_ENABLED) return;

  const entry = {
    t: Math.round(performance.now()),
    label,
    path: window.location.pathname + window.location.search,
    ...data,
  };

  console.log(`[AUTHDBG] ${entry.t}ms ${label}`, data);

  try {
    const arr = JSON.parse(localStorage.getItem('__authdbg') || '[]');
    arr.push(entry);
    localStorage.setItem('__authdbg', JSON.stringify(arr.slice(-200)));
  } catch {}
};

function readSession() {
  try {
    return JSON.parse(localStorage.getItem('session') || 'null');
  } catch {
    return null;
  }
}

function saveSession(who) {
  if (!who) {
    localStorage.removeItem('session');
    return;
  }
  localStorage.setItem('session', JSON.stringify(who));
}

function readPendingInviteToken() {
  try {
    return (localStorage.getItem('pendingInviteToken') || '').trim();
  } catch {
    return '';
  }
}

async function fetchWhoAmI() {
  const { data } = await api.get('/api/auth/whoami', { skipCustomer: true });
  return data;
}

function InnerAuthProvider({ children }) {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [session, setSession] = useState(readSession());
  const [ready, setReady] = useState(false);
  const didHydrate = useRef(false);
  const [hydrateTick, setHydrateTick] = useState(0);

  useEffect(() => {
    authDbg('AuthProvider mount', {
      hasSavedSession: !!readSession(),
    });
  }, []);

  const refresh = useCallback(() => {
    authDbg('refresh:called');
    didHydrate.current = false;
    setReady(false);
    setHydrateTick((x) => x + 1);
  }, []);

  useEffect(() => {
    const savedSession = readSession();

    authDbg('hydrate:effect', {
      isLoading,
      isAuthenticated,
      didHydrate: didHydrate.current,
      hydrateTick,
      hasSession: !!savedSession,
      sessionRole: savedSession?.role ?? null,
    });

    if (isLoading) return;

    if (!isAuthenticated) {
      authDbg('session:clear', { reason: 'not_authenticated' });
      didHydrate.current = false;
      clearClientSession();
      setSession(null);
      setReady(true);
      return;
    }

    if (didHydrate.current) {
      authDbg('hydrate:skip', { reason: 'didHydrate_already_true' });
      setReady(true);
      return;
    }
    didHydrate.current = true;

    let cancelled = false;

    (async () => {
      let suppressReady = false;

      try {
        authDbg('token:get start');
        const token = await getAccessTokenSilently();
        if (cancelled) return;

        authDbg('token:get ok', { hasToken: !!token });
        localStorage.setItem('accessToken', token);

        const pendingInviteToken = readPendingInviteToken();
        let inviteAcceptedThisRun = false;
        let inviteAttemptsKey = null;

        if (pendingInviteToken) {
          inviteAttemptsKey = `__invite_accept_attempts__${pendingInviteToken}`;
          const attempts = Number(localStorage.getItem(inviteAttemptsKey) || '0');

          if (attempts < 3) {
            try {
              localStorage.setItem(inviteAttemptsKey, String(attempts + 1));

              authDbg('invite:accept start', {
                attempts: attempts + 1,
                mode: 'pre_whoami',
              });

              await api.post(
                '/api/signup/accept',
                { token: pendingInviteToken },
                { skipCustomer: true }
              );

              authDbg('invite:accept ok', { mode: 'pre_whoami' });
              inviteAcceptedThisRun = true;
            } catch (e2) {
              const s2 = e2?.status ?? e2?.response?.status ?? null;
              const d2 = e2?.data ?? e2?.response?.data ?? null;
              const msg2 = String(d2?.message || e2?.message || e2);

              authDbg('invite:accept failed', {
                status: s2,
                message: msg2,
                mode: 'pre_whoami',
              });

              if (s2 === 400 && msg2.toLowerCase().includes('invite already used')) {
                authDbg('invite:accept already_used', { mode: 'pre_whoami' });
                localStorage.removeItem('pendingInviteToken');
                localStorage.removeItem(inviteAttemptsKey);
              }
            }
          } else {
            authDbg('invite:accept skip', {
              reason: 'max_attempts',
              attempts,
              mode: 'pre_whoami',
            });
          }
        }

        authDbg('whoami:start', { hasToken: !!token });
        const whoRaw = await fetchWhoAmI();
        if (cancelled) return;

        authDbg('whoami:ok', {
          role: whoRaw?.role ?? null,
          customerId: whoRaw?.customerId ?? null,
          customerName: whoRaw?.customerName ?? null,
          userId: whoRaw?.userId ?? null,
        });

        const who = buildAuthProviderSession(whoRaw);

        if (!who.userId || !who.role) throw new Error('whoami missing userId/role');

        saveSession(who);
        setSession(who);
        authDbg('session:set', {
          role: who.role,
          customerId: who.customerId,
          customerName: who.customerName ?? null,
          userId: who.userId,
        });

        if (inviteAcceptedThisRun && pendingInviteToken && inviteAttemptsKey) {
          localStorage.removeItem('pendingInviteToken');
          localStorage.removeItem(inviteAttemptsKey);
          authDbg('invite:token cleared', { mode: 'post_whoami' });
        }
      } catch (err) {
        const status = err?.status ?? err?.response?.status ?? null;
        const data = err?.data ?? err?.response?.data ?? null;
        const code = data?.code ?? null;
        const sub = data?.sub ?? null;

        authDbg('hydrate:catch', {
          status,
          code,
          hasSub: !!sub,
          message: String(err?.message || err),
        });

        if (status === 409 && code === 'SIGNUP_REQUIRED') {
          authDbg('signup_required', {
            hasPendingInviteToken: !!readPendingInviteToken(),
            hasSub: !!sub,
          });

          localStorage.removeItem('session');

          const pendingInviteToken = readPendingInviteToken();

          if (pendingInviteToken) {
            const attemptsKey = `__invite_accept_attempts__${pendingInviteToken}`;
            const attempts = Number(localStorage.getItem(attemptsKey) || '0');

            if (attempts < 3) {
              try {
                localStorage.setItem(attemptsKey, String(attempts + 1));

                authDbg('invite:accept start', { attempts: attempts + 1 });

                await api.post(
                  '/api/signup/accept',
                  { token: pendingInviteToken },
                  { skipCustomer: true }
                );

                authDbg('invite:accept ok');

                localStorage.removeItem('pendingInviteToken');
                localStorage.removeItem(attemptsKey);

                suppressReady = true;
                didHydrate.current = false;
                setReady(false);
                setHydrateTick((x) => x + 1);
                return;
              } catch (e2) {
                const s2 = e2?.status ?? e2?.response?.status ?? null;
                const d2 = e2?.data ?? e2?.response?.data ?? null;
                const msg2 = String(d2?.message || e2?.message || e2);

                authDbg('invite:accept failed', {
                  status: s2,
                  message: msg2,
                });

                if (s2 === 400 && msg2.toLowerCase().includes('invite already used')) {
                  localStorage.removeItem('pendingInviteToken');
                  localStorage.removeItem(attemptsKey);

                  suppressReady = true;
                  didHydrate.current = false;
                  setReady(false);
                  setHydrateTick((x) => x + 1);
                  return;
                }
              }
            }

            const pendingSession = { pendingInvite: true, sub: sub || null };
            setSession(pendingSession);
            authDbg('session:set', { pendingInvite: true, hasSub: !!pendingSession.sub });

            if (!window.location.pathname.startsWith('/invite')) {
              authDbg('nav:replace', { to: '/invite', hasToken: true });
              window.location.replace(`/invite?token=${encodeURIComponent(pendingInviteToken)}`);
              return;
            }
            return;
          }

          authDbg('session:clear', { reason: 'signup_required_no_pending_invite' });
          setSession(null);

          const qs = sub ? `?sub=${encodeURIComponent(sub)}` : '';
          if (!window.location.pathname.startsWith('/signup')) {
            authDbg('nav:replace', { to: '/signup', hasSub: !!sub });
            window.location.replace(`/signup${qs}`);
            return;
          }
          return;
        }

        authDbg('session:clear', { reason: 'hydrate_error', status, code });
        clearClientSession();
        setSession(null);
      } finally {
        if (!cancelled && !suppressReady) {
          authDbg('ready:set', { ready: true });
          setReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      authDbg('hydrate:cancelled');
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, hydrateTick]);

  const login = useCallback((options) => loginWithRedirect(options), [loginWithRedirect]);

  const logout = useCallback(() => {
    authDbg('logout:called');
    authDbg('session:clear', { reason: 'logout' });
    clearClientSession();
    setSession(null);
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout]);

  const value = useMemo(
    () => ({ session, setSession, logout, login, ready, refresh }),
    [session, ready, login, logout, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function AuthProvider({ children }) {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </Auth0Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);